import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Hourglass as HourglassIcon, Lock as LockIcon, Mail as MailIcon, Pencil as PencilIcon, Save as SaveIcon } from 'lucide-react';
import { usersService } from '../../services/api';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MIN_PASSWORD_LENGTH = 8;

/** A readable message from an API error (validation details first). */
export function apiErrorMessage(error, fallback) {
  const data = error?.response?.data || error;
  const detail = Array.isArray(data?.details) ? data.details.map((item) => item?.message).find(Boolean) : null;
  const message = detail || data?.error || data?.message || error?.errorMessage || error?.message;
  return typeof message === 'string' && message ? message : fallback;
}

const labelStyle = { display: 'block', fontSize: '14px', fontWeight: '600', color: '#36433B', marginBottom: '6px' };
const inputStyle = { width: '100%', padding: '10px 12px', border: '2px solid #1A3E2A', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
const valueStyle = { padding: '10px 12px', backgroundColor: '#F5F3EB', border: '1px solid #E3DFD3', borderRadius: '6px', fontSize: '14px' };
const primaryButton = (disabled) => ({
  backgroundColor: disabled ? '#A5A89B' : '#1A3E2A',
  color: 'white',
  border: 'none',
  padding: '8px 16px',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});
const secondaryButton = {
  backgroundColor: '#EEEBE1',
  color: '#36433B',
  border: '1px solid #D2CDBF',
  padding: '8px 16px',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
};
const inlineButton = {
  backgroundColor: 'transparent',
  color: '#1A3E2A',
  border: '1px solid #1A3E2A',
  padding: '6px 10px',
  borderRadius: '6px',
  fontSize: '12px',
  fontWeight: '600',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  whiteSpace: 'nowrap',
};

const SAVE_LABEL = { details: 'Save Changes', email: 'Change Email', password: 'Change Password' };
const TITLE = { view: 'View and manage your account information', details: 'Edit your details', email: 'Change your email address', password: 'Change your password' };

function Field({ id, label, children }) {
  return (
    <div>
      <label htmlFor={id} style={labelStyle}>
        {label}
      </label>
      {children}
    </div>
  );
}

function ProfileModal({ user, onClose, onUserUpdated }) {
  const [account, setAccount] = useState({
    id: user?.id,
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    role: user?.role || '',
  });
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('view');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [details, setDetails] = useState({ name: '', phone: '' });
  const [emailForm, setEmailForm] = useState({ newEmail: '', currentPassword: '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  useEffect(() => {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Always show what the server has, not what was cached at sign-in
  useEffect(() => {
    let cancelled = false;
    usersService
      .getMyProfile()
      .then((response) => {
        const profile = response?.data;
        if (!cancelled && profile?.id) setAccount(profile);
      })
      .catch((error) => {
        if (!cancelled) toast.error(apiErrorMessage(error, 'Could not load your profile'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [onClose]);

  const openMode = (next) => {
    setFormError(null);
    if (next === 'details') setDetails({ name: account.name || '', phone: account.phone || '' });
    if (next === 'email') setEmailForm({ newEmail: '', currentPassword: '' });
    if (next === 'password') setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setMode(next);
  };

  const run = async (work) => {
    setSaving(true);
    setFormError(null);
    try {
      await work();
    } catch (error) {
      // Shown inline above the buttons, next to the fields it is about
      setFormError(apiErrorMessage(error, 'Something went wrong. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const saveDetails = () => {
    const name = details.name.trim();
    const phone = details.phone.trim();
    if (name.length < 2) return setFormError('Name must be at least 2 characters');
    if (phone && !/^\+?[\d\s()-]{6,20}$/.test(phone)) return setFormError('Enter a valid phone number');
    return run(async () => {
      const response = await usersService.updateMyProfile({ name, phone });
      const updated = response?.data || { ...account, name, phone };
      setAccount(updated);
      onUserUpdated?.({ name: updated.name, phone: updated.phone });
      toast.success('Profile updated successfully');
      setMode('view');
    });
  };

  const saveEmail = () => {
    const newEmail = emailForm.newEmail.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(newEmail)) return setFormError('Enter a valid email address');
    if (newEmail === String(account.email || '').toLowerCase()) return setFormError('That is already your email address');
    if (!emailForm.currentPassword) return setFormError('Enter your current password');
    return run(async () => {
      const response = await usersService.changeMyEmail({ newEmail, currentPassword: emailForm.currentPassword });
      const updated = response?.data?.user || { ...account, email: newEmail };
      if (response?.data?.token) localStorage.setItem('token', response.data.token);
      setAccount(updated);
      onUserUpdated?.({ email: updated.email });
      toast.success(`Email changed. Sign in with ${updated.email} from now on.`);
      setMode('view');
    });
  };

  const savePassword = () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    if (!currentPassword) return setFormError('Enter your current password');
    if (newPassword.length < MIN_PASSWORD_LENGTH) return setFormError(`The new password must be at least ${MIN_PASSWORD_LENGTH} characters`);
    if (newPassword !== confirmPassword) return setFormError("The new passwords don't match");
    if (newPassword === currentPassword) return setFormError('The new password must be different from the current one');
    return run(async () => {
      await usersService.changeMyPassword({ currentPassword, newPassword, confirmPassword });
      toast.success('Password changed successfully');
      setMode('view');
    });
  };

  const save = mode === 'details' ? saveDetails : mode === 'email' ? saveEmail : savePassword;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const onEnter = (event) => {
    if (event.key === 'Enter' && !saving) {
      event.preventDefault();
      save();
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      SUPERADMIN: '#8E3628',
      PROPERTYADMIN: '#163626',
      STAFF: '#1A3E2A',
      GUEST: '#5F6B61',
    };
    return colors[role] || '#5F6B61';
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px',
        backdropFilter: 'blur(2px)',
      }}
      onClick={handleBackdropClick}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="My Profile"
        style={{
          backgroundColor: '#E9DFCE',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: 'calc(100vh - 40px)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 75px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ background: '#1A3E2A', color: 'white', padding: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {/* Explicit colours: index.css gives every h2 and p a dark grey */}
            <h2 style={{ margin: '0 0 4px 0', fontSize: '24px', fontWeight: '700', color: '#FFFFFF' }}>My Profile</h2>
            <p style={{ margin: 0, fontSize: '14px', color: 'rgba(255, 255, 255, 0.85)' }}>{TITLE[mode]}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              padding: '8px',
              borderRadius: '6px',
              fontSize: '18px',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto' }}>
          {/* Role Badge */}
          <div style={{ textAlign: 'center', marginBottom: '24px', padding: '16px', backgroundColor: '#F5F3EB', borderRadius: '8px', border: '1px solid #E3DFD3' }}>
            <div
              style={{
                display: 'inline-block',
                padding: '6px 12px',
                backgroundColor: getRoleColor(account.role),
                color: 'white',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '600',
              }}
            >
              {account.role}
            </div>
            {loading && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#5F6B61' }}>
                <HourglassIcon size="1em" aria-hidden="true" /> Loading your profile…
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {mode === 'view' && (
              <>
                <div>
                  <span style={labelStyle}>Full Name</span>
                  <div style={valueStyle}>{account.name || 'Not provided'}</div>
                </div>

                <div>
                  <span style={labelStyle}>Email Address</span>
                  <div style={{ ...valueStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                    <span style={{ wordBreak: 'break-all' }}>{account.email || 'Not provided'}</span>
                    <button type="button" style={inlineButton} onClick={() => openMode('email')} disabled={loading}>
                      <MailIcon size="1em" aria-hidden="true" /> Change email
                    </button>
                  </div>
                </div>

                <div>
                  <span style={labelStyle}>Phone Number</span>
                  <div style={valueStyle}>{account.phone || 'Not provided'}</div>
                </div>

                <div>
                  <span style={labelStyle}>Password</span>
                  <div style={{ ...valueStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                    <span>••••••••</span>
                    <button type="button" style={inlineButton} onClick={() => openMode('password')} disabled={loading}>
                      <LockIcon size="1em" aria-hidden="true" /> Change password
                    </button>
                  </div>
                </div>

                {/* Account Info */}
                <div style={{ marginTop: '8px', padding: '16px', backgroundColor: '#F0F3EE', border: '1px solid #C4D4C8', borderRadius: '8px' }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#0F2619' }}>Account Information</h4>
                  <div style={{ fontSize: '12px', color: '#0F2619', lineHeight: '1.6' }}>
                    <div>
                      Account Type: <strong>{account.role}</strong>
                    </div>
                    <div>
                      User ID: <strong>{account.id?.slice(-8) || 'N/A'}</strong>
                    </div>
                    <div>
                      Status: <strong style={{ color: '#163626' }}>{account.isVerified === false ? 'Not verified' : 'Active'}</strong>
                    </div>
                  </div>
                </div>
              </>
            )}

            {mode === 'details' && (
              <>
                <Field id="profile-edit-name" label="Full Name">
                  <input
                    id="profile-edit-name"
                    type="text"
                    value={details.name}
                    onChange={(e) => setDetails((current) => ({ ...current, name: e.target.value }))}
                    onKeyDown={onEnter}
                    autoComplete="name"
                    style={inputStyle}
                  />
                </Field>
                <Field id="profile-edit-phone" label="Phone Number">
                  <input
                    id="profile-edit-phone"
                    type="tel"
                    value={details.phone}
                    onChange={(e) => setDetails((current) => ({ ...current, phone: e.target.value }))}
                    onKeyDown={onEnter}
                    placeholder="Enter your phone number"
                    autoComplete="tel"
                    style={inputStyle}
                  />
                </Field>
              </>
            )}

            {mode === 'email' && (
              <>
                <div>
                  <span style={labelStyle}>Current Email</span>
                  <div style={{ ...valueStyle, color: '#5F6B61', wordBreak: 'break-all' }}>{account.email}</div>
                </div>
                <Field id="profile-new-email" label="New Email Address">
                  <input
                    id="profile-new-email"
                    type="email"
                    value={emailForm.newEmail}
                    onChange={(e) => setEmailForm((current) => ({ ...current, newEmail: e.target.value }))}
                    onKeyDown={onEnter}
                    autoComplete="email"
                    style={inputStyle}
                  />
                </Field>
                <Field id="profile-email-password" label="Current Password">
                  <input
                    id="profile-email-password"
                    type="password"
                    value={emailForm.currentPassword}
                    onChange={(e) => setEmailForm((current) => ({ ...current, currentPassword: e.target.value }))}
                    onKeyDown={onEnter}
                    autoComplete="current-password"
                    style={inputStyle}
                  />
                </Field>
                <p style={{ margin: 0, fontSize: '12px', color: '#5F6B61' }}>You will sign in with the new address from now on.</p>
              </>
            )}

            {mode === 'password' && (
              <>
                <Field id="profile-current-password" label="Current Password">
                  <input
                    id="profile-current-password"
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm((current) => ({ ...current, currentPassword: e.target.value }))}
                    onKeyDown={onEnter}
                    autoComplete="current-password"
                    style={inputStyle}
                  />
                </Field>
                <Field id="profile-new-password" label="New Password">
                  <input
                    id="profile-new-password"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm((current) => ({ ...current, newPassword: e.target.value }))}
                    onKeyDown={onEnter}
                    autoComplete="new-password"
                    style={inputStyle}
                  />
                </Field>
                <Field id="profile-confirm-password" label="Confirm New Password">
                  <input
                    id="profile-confirm-password"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm((current) => ({ ...current, confirmPassword: e.target.value }))}
                    onKeyDown={onEnter}
                    autoComplete="new-password"
                    style={inputStyle}
                  />
                </Field>
                <p style={{ margin: 0, fontSize: '12px', color: '#5F6B61' }}>At least {MIN_PASSWORD_LENGTH} characters.</p>
              </>
            )}

            {formError && mode !== 'view' && (
              <div role="alert" style={{ padding: '10px 12px', backgroundColor: '#F2D9D3', border: '1px solid #E0B6AC', borderRadius: '6px', fontSize: '13px', color: '#8E3628' }}>
                {formError}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #E3DFD3', backgroundColor: '#F5F3EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {mode === 'view' ? (
            <>
              <div style={{ fontSize: '12px', color: '#5F6B61' }}>Click edit to modify your information</div>
              <button type="button" onClick={() => openMode('details')} disabled={loading} style={primaryButton(loading)}>
                <PencilIcon size="1em" aria-hidden="true" />
                Edit Profile
              </button>
            </>
          ) : (
            <div style={{ display: 'flex', gap: '12px', width: '100%', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => openMode('view')} disabled={saving} style={secondaryButton}>
                Cancel
              </button>
              <button type="button" onClick={save} disabled={saving} style={primaryButton(saving)}>
                {saving ? <HourglassIcon size="1em" aria-hidden="true" /> : <SaveIcon size="1em" aria-hidden="true" />}
                {saving ? 'Saving...' : SAVE_LABEL[mode]}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfileModal;
